import Image from 'next/image';
import Link from 'next/link';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import { useTranslation } from '@hooks';
import { Subtitle3, Caption2, H3, Text2 } from '@components/styles/Typography';
import { colors } from 'styles/variables';
import { useState } from 'react';
import { CloseButton, RightModal } from '@components/ui';
import Size from './Size';

const ProductColors = ({ colorsPhoto, withTitle, productID, currentColor }) => {
    const { t, locale } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const router = useRouter();
    const isProdRequest = !!router.query['production-request'];

    const openModal = () => {
        setIsOpen(true);
    };
    const closeModal = () => {
        setIsOpen(false);
    };
    return (
        <Wrapper data-testid="product-colors">
            {withTitle && (
                <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                    <TitleWrapper>
                        <Subtitle3>{t('productColor')}:</Subtitle3>
                        <Caption2>{currentColor[locale]}</Caption2>
                    </TitleWrapper>
                    <Text2
                        onClick={openModal}
                        style={{
                            textDecoration: 'underline',
                            color: colors.bgGray25,
                            cursor: 'pointer',
                        }}
                    >
                        {t('productSizeTable')}
                    </Text2>
                </div>
            )}
            <ColorWrapper>
                {colorsPhoto?.map((photo) => {
                    if (isProdRequest) {
                        return (
                            <Link
                                key={photo.id}
                                to={`/${t('productSlug')}/${
                                    photo.slug[locale]
                                }?production-request=true`}
                                href={`/${t('productSlug')}/${
                                    photo.slug[locale]
                                }?production-request=true`}
                                aria-label={photo.slug[locale]}
                                passHref
                            >
                                <ColorContainer>
                                    <Color
                                        isCurrentColor={photo.id === productID}
                                    >
                                        <Image
                                            src={photo.images[0].urls.thumb}
                                            alt={photo.slug[locale]}
                                            width={120}
                                            height={160}
                                            loading="eager"
                                        />
                                    </Color>
                                    <ColorName
                                        isSelected={photo.id === productID}
                                    >
                                        {photo.colour?.name[locale]}
                                    </ColorName>
                                </ColorContainer>
                            </Link>
                        );
                    }

                    return photo.pack_count > 0 ? (
                        <Link
                            key={photo.id}
                            to={`/${t('productSlug')}/${photo.slug[locale]}`}
                            href={`/${t('productSlug')}/${photo.slug[locale]}`}
                            aria-label={photo.slug[locale]}
                            passHref
                        >
                            <ColorContainer>
                                <Color isCurrentColor={photo.id === productID}>
                                    <Image
                                        src={photo.images[0].urls.thumb}
                                        alt={photo.slug[locale]}
                                        width={120}
                                        height={160}
                                        loading="eager"
                                    />
                                </Color>
                                <ColorName isSelected={photo.id === productID}>
                                    {photo.colour?.name[locale]}
                                </ColorName>
                            </ColorContainer>
                        </Link>
                    ) : null;
                })}
            </ColorWrapper>
            <RightModal
                isOpen={isOpen}
                closeModal={closeModal}
                noOverlay
                width="44vw"
            >
                <TableWrapper>
                    <CloseButton
                        close={closeModal}
                        right
                        top="42px"
                        padding="0"
                    />
                    <H3>{t('sizeTable')}</H3>
                    <Size />
                </TableWrapper>
            </RightModal>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    position: relative;
    width: 100%;
    overflow-x: scroll;
    overflow-y: hidden;
    margin-top: 8px;
    margin-bottom: 24px;
    min-height: 200px;
    scrollbar-width: none;

    ::-webkit-scrollbar {
        display: none;
        scrollbar-width: none;
    }

    @media (max-width: 1024px) {
        margin-top: 20px;
        margin-bottom: auto;
        display: flex;
        flex-direction: column;
    }
`;
const ColorWrapper = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: flex-start;
    margin-top: 16px;
    overflow-x: scroll;
    overflow-y: hidden;
    gap: 16px;
    scrollbar-width: none;
    padding: 8px 0;

    ::-webkit-scrollbar {
        display: none;
        scrollbar-width: none;
    }
`;
const TitleWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;
const Color = styled.div`
    width: 120px;
    min-width: 120px;
    height: 160px;
    border: 3px solid
        ${({ isCurrentColor }) => (isCurrentColor ? '#000' : '#E5E5E5')};
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;

    &:hover {
        border-color: ${({ isCurrentColor }) =>
            isCurrentColor ? '#000' : '#999'};
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    img {
        border: none;
        object-fit: cover;
        transition: transform 0.3s ease;
    }

    &:hover img {
        transform: scale(1.05);
    }
`;

const TableWrapper = styled.div`
    width: fit-content;
    margin: 150px auto 0;
`;

const ColorContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
`;

const ColorName = styled(Text2)`
    font-size: 12px;
    font-weight: ${({ isSelected }) => (isSelected ? 'bold' : 'normal')};
    color: ${({ isSelected, theme }) =>
        isSelected ? theme.colors.primary : theme.colors.bgGray50};
    text-align: center;
    margin-top: 4px;
`;

export default ProductColors;
